import { useCallback, useEffect, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { gsdPerTreatment } from '@/constants/gsd-per-treatment'
import { useCompanies } from '@/hooks/use-companies'
import { useDiagnosisRegistrationStore } from '@/pages/demands/contexts/diagnosis-registration-store-context'
import { DefaultSelectFilesFormData } from '@/pages/demands/steps/select-files/hooks/use-select-files-form'

import {
  checkImageIntegrity,
  validateMosaicFile,
  validatePerimetersFiles,
} from '../utils'

export const useValidationInputs = (
  methods: UseFormReturn<DefaultSelectFilesFormData>,
) => {
  const { watch, setValue, setError, clearErrors, resetField } = methods

  const treatment = useDiagnosisRegistrationStore(
    (state) => state.registerData?.treatment!,
  )

  const [perimeterInput, mosaicInput, jpgRgbInput, jpgThermalInput] = watch([
    'perimeter.input',
    'mosaic.input',
    'rgb.input',
    'thermal.input',
  ])
  const { permissions } = useCompanies()
  const jpgInputLimit = useMemo(() => {
    if (permissions.WeedAccess) return 10000
    return 5000
  }, [permissions.WeedAccess])

  const onPerimeterInputChange = useCallback(async (files?: File[]) => {
    clearErrors('perimeter')
    resetField('perimeter.results', {
      defaultValue: null as any,
    })

    if (!files?.length) return

    try {
      const { geojson, bounds, filename, interestArea } =
        await validatePerimetersFiles(files)

      setValue('perimeter.results', {
        filename,
        area: interestArea,
        geojson,
        lat_long: [bounds.lat, bounds.lng],
      })
    } catch (error: any) {
      setError('perimeter.input', {
        type: 'custom',
        message: error.message,
      })
    }
  }, [])

  const onMosaicInputChange = useCallback(async (files?: File[]) => {
    clearErrors('mosaic')
    resetField('mosaic.results', {
      defaultValue: null as any,
    })

    if (!files?.length) return

    try {
      const { min, max } = gsdPerTreatment[treatment]

      const results = await validateMosaicFile(files[0], min, max)

      setValue('mosaic.results', results)
    } catch (error: any) {
      setError('mosaic.input', {
        type: 'custom',
        message: error.message,
      })
    }
  }, [])

  const onJpegInputChange = useCallback(
    async (inputBase: 'rgb' | 'thermal', files: File[]) => {
      clearErrors(inputBase)
      resetField(`${inputBase}.results`, {
        defaultValue: null as any,
      })

      if (!files?.length) return

      try {
        if (files.length > jpgInputLimit)
          throw new Error(
            `Você ultrapassou o limite de ${jpgInputLimit} imagens. Verifique seus arquivos e tente novamente. `,
          )

        try {
          for (const file of files) {
            await checkImageIntegrity(file)
          }
        } catch {
          throw new Error(
            'Uma ou mais imagens podem estar corrompidas. Verifique e tente novamente.',
          )
        }

        setValue(`${inputBase}.results`, {
          image_count: files.length,
          images_size: files.reduce((acc, file) => acc + file.size, 0),
        })
      } catch (error: any) {
        setError(`${inputBase}.input`, {
          type: 'custom',
          message: error.message,
        })
      }
    },
    [],
  )

  useEffect(() => {
    onPerimeterInputChange(perimeterInput)
  }, [perimeterInput])

  useEffect(() => {
    onMosaicInputChange(mosaicInput)
  }, [mosaicInput])

  useEffect(() => {
    onJpegInputChange('rgb', jpgRgbInput)
  }, [jpgRgbInput])

  useEffect(() => {
    onJpegInputChange('thermal', jpgThermalInput)
  }, [jpgThermalInput])
}
